/**
 * Supabase Network Interceptor
 *
 * Wraps the global fetch to intercept Supabase network errors and show
 * a user-friendly modal instead of runtime errors.
 */

let renderer: any = null;
let isModalShowing = false;
let isInterceptorInstalled = false;
let handledErrors = new WeakSet(); // Track errors we've already handled
let isNetworkPaused = false; // When true, short-circuit outbound fetches

export function setNetworkPaused(paused: boolean) {
  isNetworkPaused = paused;
}

/**
 * Install fetch interceptor early (before Supabase client creation)
 * Call this BEFORE createClient() so Supabase uses the wrapped fetch
 */
export function installFetchInterceptorEarly() {
  if (!isInterceptorInstalled) {
    installFetchInterceptor();
    installUnhandledRejectionHandler();
    isInterceptorInstalled = true;
  }
}

/**
 * Initialize the interceptor with renderer for modal display
 * Call this later when renderer is available
 */
export function initSupabaseInterceptor(rendererInstance: any) {
  renderer = rendererInstance;

  // Install interceptor if not already installed (backwards compatibility)
  if (!isInterceptorInstalled) {
    installFetchInterceptor();
    isInterceptorInstalled = true;
  }
}

/**
 * Check if an error is network-related
 */
function isNetworkError(error: any): boolean {
  if (!error) return false;

  const errorMessage = error.message?.toLowerCase() || '';
  const errorName = error.name?.toLowerCase() || '';

  // Network/fetch errors
  if (errorName === 'typeerror' && errorMessage.includes('fetch')) return true;
  if (errorMessage.includes('failed to fetch')) return true;
  if (errorMessage.includes('network')) return true;
  if (errorMessage.includes('offline')) return true;
  if (errorMessage.includes('load failed')) return true;
  if (errorMessage.includes('timeout')) return true;
  if (errorMessage.includes('enotfound')) return true;
  if (errorMessage.includes('econnrefused')) return true;

  return false;
}

/**
 * Show connection error modal
 */
function showConnectionError() {
  if (isModalShowing) {
    return;
  }

  if (renderer && typeof renderer.showModal === 'function') {
    isModalShowing = true;

    renderer.showModal({
      title: "Connection Issue",
      body: "Unable to connect to the server. Please check your internet connection and try again.",
      button: "OK",
      onClose: () => {
        isModalShowing = false;
      }
    });
  } else {
    console.warn('[SupabaseInterceptor] Renderer not initialized yet, cannot show modal (will show on next error after renderer is ready)');
  }
}

/**
 * Install fetch interceptor to catch network errors before they become runtime errors
 */
function installFetchInterceptor() {
  const originalFetch = window.fetch;

  window.fetch = async function(...args) {
    if (isNetworkPaused) {
      // Drop outbound calls while paused to avoid background churn
      return new Response(
        JSON.stringify([]),
        {
          status: 200,
          statusText: 'OK',
          headers: { 'Content-Type': 'application/json' }
        }
      );
    }

    try {
      const response = await originalFetch.apply(this, args);

      // Check if this is a Supabase request
      const url = args[0] as string;
      const isSupabaseRequest = url?.includes(process.env.REACT_APP_SUPA_URL || '');

      // If it's a Supabase request and response is not ok, check for network errors
      if (isSupabaseRequest && !response.ok) {
        // Clone the response so we can read it without consuming the original
        const responseClone = response.clone();

        // Try to read error details
        try {
          const errorData = await responseClone.json();

          // Check if this looks like a network/connection error
          if (errorData?.code === 'NETWORK_ERROR' ||
              errorData?.message?.toLowerCase().includes('network') ||
              errorData?.message?.toLowerCase().includes('connection')) {
            showConnectionError();
          }
        } catch (e) {
          // If we can't parse the error, that's fine - return the original response
        }
      }

      return response;
    } catch (error: any) {
      // Check if this is a Supabase request
      const url = args[0] as string;
      const isSupabaseRequest = url?.includes(process.env.REACT_APP_SUPA_URL || '');
      if (isSupabaseRequest && isNetworkError(error)) {
        console.warn('[SupabaseInterceptor] Network error detected for Supabase request (handled with modal)');
        showConnectionError();

        // Return a fake successful empty response to prevent console.error calls
        // This way Supabase queries will just return empty results instead of errors
        // The modal has already informed the user about the connection issue
        return new Response(
          JSON.stringify([]), // Empty array for list queries
          {
            status: 200,
            statusText: 'OK',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      // Re-throw other errors
      throw error;
    }
  };

}

/**
 * Install global unhandled rejection handler to suppress browser errors
 * for network issues we've already shown a modal for
 */
function installUnhandledRejectionHandler() {
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;

    // Check if this is an error we already handled
    if (handledErrors.has(error)) {
      event.preventDefault();
      return;
    }

    // Also check if this is a Supabase-wrapped network error
    // Supabase wraps errors like: { message: "TypeError: Failed to fetch", details: "...", code: "", hint: "" }
    if (error && typeof error === 'object') {
      const message = error.message?.toLowerCase() || '';
      const details = error.details?.toLowerCase() || '';

      // Check if the wrapped error is a network error
      const isWrappedNetworkError =
        message.includes('failed to fetch') ||
        message.includes('network') ||
        message.includes('typeerror: fetch') ||
        details.includes('failed to fetch') ||
        details.includes('network error') ||
        details.includes('typeerror: fetch');

      if (isWrappedNetworkError) {
        event.preventDefault();
        return;
      }
    }

  });
}

/**
 * Reset the modal state (useful for testing)
 */
export function resetInterceptorState() {
  isModalShowing = false;
}
